import { HttpClient, HttpEvent, HttpParams } from "@angular/common/http";
import { Inject, Injectable } from "@angular/core";
import { Observable } from "rxjs";
import {
  StaffPayrollPeriodRequest,
  StaffPayrollPeriodResponse,
  StaffPayrollPeriodStatus,
  StaffPayrollRunResponse,
  StaffManualPayslipRequest,
  StaffManualPayslipResponse,
  StaffManualPayslipPage,
  StaffPayrollImportSummary,
  StaffPayrollImportPage,
  StaffPayrollImportReviewPage,
  StaffPayrollImportItem,
  StaffPayrollImportResult,
  StaffPayslipRequest,
  StaffPayslipResponse,
  StaffPayslipStatus,
} from "../model/payroll.model";
import { BASE_PATH } from "../variables";

@Injectable({ providedIn: "root" })
export class StaffPayrollService {
  private readonly apiUrl: string;

  constructor(
    private readonly http: HttpClient,
    @Inject(BASE_PATH) basePath: string,
  ) {
    this.apiUrl = `${basePath.replace(/\/$/, "")}/api/v1`;
  }

  createManualPayslip(
    request: StaffManualPayslipRequest,
    file: File,
  ): Observable<StaffManualPayslipResponse> {
    const body = new FormData();
    body.append("request", new Blob([JSON.stringify(request)], { type: "application/json" }));
    body.append("file", file, file.name);
    return this.http.post<StaffManualPayslipResponse>(
      `${this.apiUrl}/payment-slips`,
      body,
    );
  }

  retryManualPayslipDocument(id: number, file: File): Observable<StaffManualPayslipResponse> {
    const body = new FormData();
    body.append("file", file, file.name);
    return this.http.post<StaffManualPayslipResponse>(
      `${this.apiUrl}/payment-slips/${id}/document`,
      body,
    );
  }

  generateImportedPayslipPdf(id: number): Observable<StaffManualPayslipResponse> {
    return this.http.post<StaffManualPayslipResponse>(
      `${this.apiUrl}/payment-slips/${id}/generate-pdf`,
      null,
    );
  }

  listManualPayslips(filters: {
    companyId: number;
    year?: number;
    month?: number;
    search?: string;
    page: number;
    size: number;
  }): Observable<StaffManualPayslipPage> {
    return this.http.get<StaffManualPayslipPage>(`${this.apiUrl}/payment-slips`, {
      params: this.params(filters),
    });
  }

  getManualPayslip(id: number): Observable<StaffManualPayslipResponse> {
    return this.http.get<StaffManualPayslipResponse>(
      `${this.apiUrl}/payment-slips/${id}`,
    );
  }

  deleteManualPayslip(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/payment-slips/${id}`);
  }

  uploadPayslipXml(companyId: number, paymentDate: string, file: File): Observable<HttpEvent<StaffPayrollImportSummary>> {
    const body = new FormData();
    body.append("file", file, file.name);
    return this.http.post<StaffPayrollImportSummary>(`${this.apiUrl}/payment-slips/imports`, body, {
      observe: "events", reportProgress: true,
      params: this.params({ companyId, paymentDate }),
    });
  }

  getPayslipImport(id: string): Observable<StaffPayrollImportSummary> {
    return this.http.get<StaffPayrollImportSummary>(`${this.apiUrl}/payment-slips/imports/${id}`);
  }

  listPayslipImportItems(id: string, page: number, size: number, filters?: {
    missingEmployee?: boolean;
    search?: string;
    year?: number;
    month?: number;
    status?: string;
  }): Observable<StaffPayrollImportPage> {
    return this.http.get<StaffPayrollImportPage>(`${this.apiUrl}/payment-slips/imports/${id}/items`, {
      params: this.params({ page, size, ...filters }),
    });
  }

  listPayslipImportReviewItems(importIds: string[], page: number, size: number, filters?: {
    search?: string;
    year?: number;
    month?: number;
    status?: string;
  }): Observable<StaffPayrollImportReviewPage> {
    let params = this.params({ page, size, ...filters });
    importIds.forEach((id) => { params = params.append("importIds", id); });
    return this.http.get<StaffPayrollImportReviewPage>(
      `${this.apiUrl}/payment-slips/imports/review/items`, { params },
    );
  }

  getPayslipImportItem(id: string, index: number): Observable<StaffPayrollImportItem> {
    return this.http.get<StaffPayrollImportItem>(`${this.apiUrl}/payment-slips/imports/${id}/items/${index}`);
  }

  updatePayslipImportItem(id: string, index: number, currencyCode: string): Observable<StaffPayrollImportSummary> {
    return this.http.patch<StaffPayrollImportSummary>(`${this.apiUrl}/payment-slips/imports/${id}/items/${index}`, {
      currencyCode,
    });
  }

  excludePayslipImportItem(id: string, index: number): Observable<StaffPayrollImportSummary> {
    return this.http.delete<StaffPayrollImportSummary>(`${this.apiUrl}/payment-slips/imports/${id}/items/${index}`);
  }

  confirmPayslipImport(id: string): Observable<StaffPayrollImportResult> {
    return this.http.post<StaffPayrollImportResult>(`${this.apiUrl}/payment-slips/imports/${id}/confirm`, null);
  }

  listPeriods(filters?: {
    companyId?: number;
    year?: number;
    month?: number;
    status?: StaffPayrollPeriodStatus;
  }): Observable<StaffPayrollPeriodResponse[]> {
    return this.http.get<StaffPayrollPeriodResponse[]>(
      `${this.apiUrl}/payroll-periods`,
      { params: this.params(filters) },
    );
  }

  getPeriod(periodId: number): Observable<StaffPayrollPeriodResponse> {
    return this.http.get<StaffPayrollPeriodResponse>(
      `${this.apiUrl}/payroll-periods/${periodId}`,
    );
  }

  createPeriod(
    request: StaffPayrollPeriodRequest,
  ): Observable<StaffPayrollPeriodResponse> {
    return this.http.post<StaffPayrollPeriodResponse>(
      `${this.apiUrl}/payroll-periods`,
      request,
    );
  }

  calculatePeriod(periodId: number): Observable<StaffPayrollRunResponse> {
    return this.http.post<StaffPayrollRunResponse>(
      `${this.apiUrl}/payroll-periods/${periodId}/calculate`,
      null,
    );
  }

  publishPeriod(periodId: number): Observable<StaffPayrollRunResponse> {
    return this.http.post<StaffPayrollRunResponse>(
      `${this.apiUrl}/payroll-periods/${periodId}/publish`,
      null,
    );
  }

  closePeriod(periodId: number): Observable<StaffPayrollPeriodResponse> {
    return this.http.post<StaffPayrollPeriodResponse>(
      `${this.apiUrl}/payroll-periods/${periodId}/close`,
      null,
    );
  }

  savePayslip(
    periodId: number,
    request: StaffPayslipRequest,
  ): Observable<StaffPayslipResponse> {
    return this.http.post<StaffPayslipResponse>(
      `${this.apiUrl}/payroll-periods/${periodId}/payslips`,
      request,
    );
  }

  listPayslips(filters?: {
    companyId?: number;
    employeeId?: number;
    year?: number;
    month?: number;
    status?: StaffPayslipStatus;
    signed?: boolean;
  }): Observable<StaffPayslipResponse[]> {
    return this.http.get<StaffPayslipResponse[]>(`${this.apiUrl}/payslips`, {
      params: this.params(filters),
    });
  }

  getPayslip(payslipId: number): Observable<StaffPayslipResponse> {
    return this.http.get<StaffPayslipResponse>(
      `${this.apiUrl}/payslips/${payslipId}`,
    );
  }

  attachDocument(
    payslipId: number,
    documentUrl: string,
  ): Observable<StaffPayslipResponse> {
    return this.http.put<StaffPayslipResponse>(
      `${this.apiUrl}/payslips/${payslipId}/document`,
      { documentUrl },
    );
  }

  signPayslip(payslipId: number): Observable<StaffPayslipResponse> {
    return this.http.post<StaffPayslipResponse>(
      `${this.apiUrl}/payslips/${payslipId}/sign`,
      null,
    );
  }

  private params(filters?: Record<string, unknown>): HttpParams {
    let params = new HttpParams();
    Object.entries(filters ?? {}).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        params = params.set(key, String(value));
      }
    });
    return params;
  }
}
